// @fastn/core - alert metric catalog + delivery-channel model.
//
// Single source of truth shared by the API alert routes (validation + the
// metric/channel catalog endpoints), the event-service evaluator (which metric
// to compute), and the dashboard/embed UI (what to render in the picker).
// Metrics an end-user can alert on. Every key maps to a scalar the evaluator
// computes over (org, end_org, window) from the same tables that back the
// widget-insights view, so what you alert on matches what you see. Grouped so
// the picker can show related metrics together.
export const ALERT_METRICS = [
    // Reliability - is it working?
    { key: "error_rate", label: "Error rate", unit: "percent", group: "Reliability", defaultCondition: "gt", suggestedThreshold: 5, description: "Percent of runs that failed in the window." },
    { key: "success_rate", label: "Success rate", unit: "percent", group: "Reliability", defaultCondition: "lt", suggestedThreshold: 95, description: "Percent of runs that succeeded - alert when it drops." },
    { key: "failed_runs", label: "Failed runs", unit: "count", group: "Reliability", defaultCondition: "gt", suggestedThreshold: 5, description: "Number of runs that failed in the window." },
    // Activity - is data flowing?
    { key: "runs", label: "Total runs", unit: "count", group: "Activity", defaultCondition: "lt", suggestedThreshold: 1, description: "Number of runs in the window - alert when activity drops off." },
    { key: "records_synced", label: "Records synced", unit: "count", group: "Activity", defaultCondition: "lt", suggestedThreshold: 1, description: "Records successfully synced - alert when a sync stalls." },
    { key: "records_failed", label: "Records failed", unit: "count", group: "Activity", defaultCondition: "gt", suggestedThreshold: 0, description: "Records that failed to sync in the window." },
    // Performance - is it fast?
    { key: "avg_run_duration", label: "Avg run time", unit: "ms", group: "Performance", defaultCondition: "gt", suggestedThreshold: 5000, description: "Average run duration in the window." },
    { key: "p95_run_duration", label: "p95 run time", unit: "ms", group: "Performance", defaultCondition: "gt", suggestedThreshold: 10000, description: "95th-percentile (slowest) run duration - catches tail latency." },
    // Health - are connections & workflows OK?
    { key: "broken_connectors", label: "Broken connectors", unit: "count", group: "Health", defaultCondition: "gt", suggestedThreshold: 0, description: "Connections that are not active right now." },
    { key: "dead_workflows", label: "Inactive workflows", unit: "count", group: "Health", defaultCondition: "gt", suggestedThreshold: 0, description: "Enabled workflows that have not run in the window." },
];
export const ALERT_METRIC_KEYS = ALERT_METRICS.map((m) => m.key);
export function alertMetricDef(key) {
    return ALERT_METRICS.find((m) => m.key === key);
}
export const ALERT_CONDITIONS = ["gt", "lt"];
/**
 * How a rule fires:
 *   threshold  - the evaluator polls the metric over the window and fires on a
 *                breach (the original behavior).
 *   instant    - the failure watcher fires on each failed/timeout execution,
 *                within ~a minute of the failure, with the workflow name, the
 *                classified reason, and a deep link. metric/condition/threshold/
 *                window are not evaluated for instant rules.
 *   code       - fires only when workflow code names the rule:
 *                fastn.alerts.send("<rule name>", { title, ... }). Never on a
 *                failure or a threshold. A rule of the other modes can be
 *                named from code too; this one exists to be nothing else.
 *   processing - the processing watcher fires when an event arrived at least
 *                `threshold` minutes ago and its workflow has not succeeded:
 *                it never ran, it failed, it is still running, or the event is
 *                still waiting in the ingest backup. The watcher does not run on
 *                Redis, so it fires during exactly the outage that stops every
 *                other alert. metric is PROCESSING_ALERT_METRIC; condition and
 *                window are not evaluated.
 */
export const ALERT_MODES = ["threshold", "instant", "code", "processing"];
/**
 * Metrics computed from workflow_executions, and therefore scopable to a set
 * of workflows via alert_rules.workflow_ids. The sync_jobs / connection /
 * workflows metrics have no per-execution grain, so a workflow scope on them
 * is rejected at the API rather than silently ignored.
 */
export const WORKFLOW_SCOPABLE_METRICS = [
    "runs", "failed_runs", "error_rate", "success_rate", "avg_run_duration", "p95_run_duration",
];
/** The stored metric for a processing rule (alert_rules.metric is NOT NULL). */
export const PROCESSING_ALERT_METRIC = "unprocessed_events";
/**
 * Minutes a processing rule waits before an event counts as not processed.
 * The floor stays above a normal standard-tier run plus Cloud Run scheduling
 * (minutes, not seconds); the ceiling keeps the watcher's scan bounded.
 */
export const PROCESSING_ALERT_MINUTES = { min: 5, max: 1440, default: 15 };
export const ALERT_WINDOWS = ["1h", "24h", "7d", "30d"];
/** Postgres interval literal for a window (used by the evaluator's queries). */
export function alertWindowToInterval(w) {
    switch (w) {
        case "1h": return "1 hour";
        case "24h": return "24 hours";
        case "7d": return "7 days";
        case "30d": return "30 days";
        default: return "24 hours";
    }
}
/** A custom email subject is one line of at most this many characters. */
export const ALERT_EMAIL_SUBJECT_MAX = 200;
/**
 * The subject an email delivery goes out with: the rule's custom subject when
 * one is set (customers filter and route alert mail on it), otherwise the
 * sender's own default. A test keeps a [TEST] marker either way, so a test is
 * never mistaken for a real alert in a filtered inbox.
 */
export function alertEmailSubject(custom, fallback, isTest) {
    const c = (custom ?? "").replace(/[\r\n\t]+/g, " ").trim().slice(0, ALERT_EMAIL_SUBJECT_MAX);
    if (!c)
        return fallback;
    return isTest && !c.startsWith("[TEST]") ? `[TEST] ${c}` : c;
}
export const ALERT_DELIVERY_TYPES = ["email", "webhook", "slack", "connection"];
/**
 * Connectors we know how to deliver to. The slugs and action slugs are the live
 * catalog's: the Slack entry named a `sendMessage` action the Slack connector
 * does not have (its post action is `createChatPostMessage`), so every Slack
 * connection delivery failed. Gmail's send action takes a whole RFC 2822
 * message in `raw`, so its input is built by the event service rather than from
 * messageField alone.
 */
export const ALERT_CONNECTION_SENDERS = {
    slack: { action: "createChatPostMessage", targetField: "channel", messageField: "text", label: "Slack", targetKind: "slackChannel" },
    googleGmail: { action: "sendMessage", targetField: "to", messageField: "raw", label: "Gmail", targetKind: "email" },
    microsoftTeams: { action: "sendChannelMessage", targetField: "channelId", messageField: "content", label: "Microsoft Teams", targetKind: "teams" },
    discord: { action: "sendMessage", targetField: "channelId", messageField: "content", label: "Discord", targetKind: "text" },
    telegram: { action: "sendMessage", targetField: "chatId", messageField: "text", label: "Telegram", targetKind: "text" },
};
/** Parse a Teams target; null when it is not one of the two accepted shapes. */
export function parseTeamsAlertTarget(target) {
    if (!target)
        return null;
    if (target.startsWith("chat:")) {
        const chatId = target.slice(5);
        return chatId ? { kind: "chat", chatId } : null;
    }
    if (target.startsWith("channel:")) {
        const rest = target.slice(8);
        const sep = rest.indexOf(":");
        if (sep <= 0)
            return null;
        const teamId = rest.slice(0, sep);
        const channelId = rest.slice(sep + 1);
        return teamId && channelId ? { kind: "channel", teamId, channelId } : null;
    }
    return null;
}
export function formatTeamsAlertTarget(t) {
    return t.kind === "chat" ? `chat:${t.chatId}` : `channel:${t.teamId}:${t.channelId}`;
}
/** Supported delivery apps, for the UI to show even before one is connected. */
export const ALERT_SENDER_APPS = Object.entries(ALERT_CONNECTION_SENDERS).map(([slug, s]) => ({ slug, label: s.label, targetField: s.targetField, targetKind: s.targetKind }));
//# sourceMappingURL=alerts.js.map